import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { fetchYahclickGraphNG } from "../../../services/vesselService";
import "flatpickr/dist/flatpickr.min.css";
import DateTimePickerYahsateServices from "@components/common/DateTimePickerYahsateServices";

const SplineAreaChartYahclickNGPing = ({ id, type, data, times, dispatch, navigate }) => {
	const [selectedRange, setSelectedRange] = useState("3");
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const [isStartDateTime, setIsStartDateTime] = useState(null);
	const [isEndDateTime, setIsEndDateTime] = useState(null);

	const [dateError, setDateError] = useState("");

	const token = useSelector((state) => state.auth.user.token);

	useEffect(() => {
		if (data) {
			setIsLoading(false);
		}
	}, [data]);

	const timeRanges = {
		3: "Last 3 Hours",
		6: "Last 6 Hours",
		12: "Last 12 Hours",
		24: "Last 24 Hours",
		168: "Last 7 Days",
	};

	const series = data.map((item) => ({
		name: `${item.label.toUpperCase()} (${item.unit})`,
		data: item.data.map((y, i) => ({
			x: new Date(times[i] * 1000),
			y: y ?? null,
		})),
	}));

	const options = {
		chart: {
			type: "area",
			height: 350,
			zoom: { enabled: false },
			toolbar: { show: false },
		},
		dataLabels: { enabled: false },
		stroke: { curve: "smooth", width: 4 },
		fill: {
			type: "solid",
			opacity: 0,
		},
		xaxis: {
			type: "datetime",
			labels: {
				formatter: (value) => {
					const date = new Date(value);
					const range = Number(selectedRange);
					function formatDate(date) {
						const pad = (n) => String(n).padStart(2, "0");
						return (
							`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
							`${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
						);
					}

					if (range <= 24) {
						return date.toLocaleTimeString("en-US", {
							month: "short",
							day: "numeric",
							hour: "numeric",
							minute: "2-digit",
							hour12: true,
						});
					} else if (range <= 168) {
						return formatDate(date);
					} else {
						return formatDate(date);
					}
				},
				style: {
					colors: "#FFFFFF",
					fontSize: "12px",
					fontWeight: "bold",
				},
			},
			axisBorder: {
				show: true,
				color: "#FFFFFF",
				height: 2,
			},
			axisTicks: {
				show: true,
				color: "#FFFFFF",
				height: 6,
			},
		},

		yaxis: {
			title: {
				// text: "Ping High Latency",
				style: {
					color: "#FFFFFF",
					fontSize: "12px",
					fontWeight: "bold",
				},
			},
			labels: {
				formatter: (value) => (value !== null && value !== undefined ? value.toFixed(0) : ""),
				style: {
					colors: "#FFFFFF",
					fontSize: "12px",
					fontWeight: "bold",
				},
			},
			axisBorder: {
				show: true,
				color: "#FFFFFF",
				width: 2,
			},
			axisTicks: {
				show: true,
				color: "#FFFFFF",
				width: 6,
			},
		},
		tooltip: {
			x: {
				format: "yyyy-MM-dd HH:mm:ss",
			},
		},
		legend: {
			position: "top",
			horizontalAlign: "right",
		},
		colors: ["#1Aa65D", "#0066cc", "#F59E0B", "#9D174D"],
	};

	useEffect(() => {
		const fetchData = async () => {
			const endTimestamp = isEndDateTime
				? Math.floor(new Date(isEndDateTime).getTime() / 1000)
				: Math.floor(Date.now() / 1000);

			const startTimestamp = isStartDateTime
				? Math.floor(new Date(isStartDateTime).getTime() / 1000)
				: endTimestamp - 7 * 24 * 60 * 60;

			if (isStartDateTime && isEndDateTime && startTimestamp > endTimestamp) {
				setDateError("Start date must be before or equal to end date.");
				return;
			}

			setIsLoading(true);
			try {
				await fetchYahclickGraphNG(
					token,
					id,
					null,
					startTimestamp,
					endTimestamp,
					"Ping_High_Latency",
					"Ping High Latency",
					dispatch,
					navigate,
				);
			} catch (err) {
				console.error("Error fetching graph:", err);
			} finally {
				setIsLoading(false);
			}
		};

		if (isStartDateTime || (isStartDateTime && isEndDateTime) || isEndDateTime) {
			fetchData();
		}
	}, [isStartDateTime, isEndDateTime]);

	const toggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen);
	};
	const handleDateRangeChange = async (startDate, endDate) => {
		const startTimestamp = Math.floor(new Date(startDate).getTime() / 1000);
		const endTimestamp = Math.floor(new Date(endDate).getTime() / 1000);

		setIsDropdownOpen(false);
		setIsLoading(true);

		try {
			await fetchYahclickGraphNG(
				token,
				id,
				null,
				startTimestamp,
				endTimestamp,
				"Ping_High_Latency",
				"Ping High Latency",
				dispatch,
				navigate,
			);
		} catch (error) {
			console.error("Error fetching data:", error);
		} finally {
			setIsLoading(false);
		}
	};
	const handleStartDateChange = (newDate) => {
		setSelectedRange("3");
		setIsStartDateTime(newDate);
		setDateError("");

		if (isEndDateTime) {
			handleDateRangeChange(newDate, isEndDateTime);
		}
	};

	const handleEndDateChange = (newDate) => {
		setSelectedRange("3");
		setIsEndDateTime(newDate);
		setDateError("");

		if (isStartDateTime) {
			handleDateRangeChange(isStartDateTime, newDate);
		}
	};

	const handleSelectRange = async (range, endDate) => {
		setSelectedRange(range);
		setIsStartDateTime(null);
		setIsEndDateTime(endDate);
		setIsDropdownOpen(false);
		setIsLoading(true);

		try {
			await fetchYahclickGraphNG(
				token,
				id,
				range,
				null,
				null,
				"Ping_High_Latency",
				"Ping High Latency",
				dispatch,
				navigate,
			);
		} catch (error) {
			console.error("Error fetching data:", error);
		} finally {
			setIsLoading(false);
		}
	};

	const refetchData = (start, end) => {
		fetchYahclickGraphNG(
			token,
			id,
			null,
			start,
			end,
			"Ping_High_Latency",
			"Ping High Latency",
			dispatch,
			navigate,
		);
	};

	return (
		<div id={`${id}-${type}-chart-yahclick`} className="w-full">
			<div className="flex w-full flex-col items-baseline gap-4 md:flex-row">
				<div className="relative md:w-auto">
					<button
						id="dropdownDefaultButton"
						onClick={toggleDropdown}
						className="inline-flex w-full items-baseline justify-between rounded-lg bg-blue-gray px-5 py-2.5 text-center font-medium text-sm text-white hover:bg-dark-blue focus:outline-none focus:ring-4 focus:ring-blue-gray md:w-auto"
						type="button"
					>
						{timeRanges[selectedRange]}
						<svg
							className="ml-2 h-3 w-3 shrink-0"
							aria-hidden="true"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 10 6"
						>
							<path
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="M1 1l4 4 4-4"
							/>
						</svg>
					</button>

					{isDropdownOpen && (
						<div
							id="dropdown"
							className="absolute right-0 left-0 z-20 mt-2 divide-y divide-gray-600 rounded-lg bg-dark-blue shadow-lg md:right-auto"
						>
							<ul className="max-h-60 overflow-y-auto py-2 text-gray-200 text-sm">
								{Object.keys(timeRanges).map((range) => (
									<li key={range}>
										<button
											onClick={() => {
												handleSelectRange(range);
												setDateError("");
											}}
											className="block w-full px-4 py-2 text-left hover:bg-blue-gray hover:text-white"
										>
											{timeRanges[range]}
										</button>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
				<div className="flex w-auto flex-col items-start gap-2 md:flex-row">
					<div className="w-full md:w-auto">
						<DateTimePickerYahsateServices
							placeholder="Select start date"
							value={isStartDateTime}
							onChange={(date) => {
								setIsStartDateTime(date);
								if (isEndDateTime && date > isEndDateTime) {
									setIsEndDateTime(null);
								}
							}}
							onClear={() => {
								setIsStartDateTime(null);
								setDateError("");
								refetchData(null, isEndDateTime);
							}}
							dateError={dateError}
							disableFutureDates={true}
							maxDate={isEndDateTime}
						/>
					</div>

					<div className="w-full md:w-auto">
						<DateTimePickerYahsateServices
							placeholder="Select end date"
							value={isEndDateTime}
							onChange={(date) => {
								if (isStartDateTime && date < isStartDateTime) {
									setDateError("End date cannot be before start date");
								} else {
									setDateError("");
									setIsEndDateTime(date);
								}
							}}
							onClear={() => {
								setIsEndDateTime(null);
								setDateError("");
								refetchData(isStartDateTime, null);
							}}
							minDate={isStartDateTime}
						/>
					</div>
				</div>
			</div>

			{isLoading ? (
				<div className="w-full py-10 text-center text-blue-gray text-xl">
					<p>Loading...</p>
				</div>
			) : (
				<ReactApexChart options={options} series={series} type="area" height={350} />
			)}
		</div>
	);
};

export default SplineAreaChartYahclickNGPing;
